import { KeyboardEvent, useEffect, useRef } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { nameof } from "ts-simple-nameof";
import { AppDialog } from "../../../../../common/components/AppDialog/AppDialog";
import { DefaultButton } from "../../../../../common/components/button/defaultButton/DefaultButton";
import { DefaultInputField } from "../../../../../common/components/forms/inputs/InputField/DefaultInputField";
import { resolveSuggestedIcon } from "../../../../linkPage/utils/linkPageIconUtility";
import {
  IStudioLinkPageItemFormModel,
  studioLinkPageItemFormDefaultValues,
  studioLinkPageItemSchema,
} from "./models/studioLinkPageItemFormModel";
import { IStudioLinkPageItem } from "./models/studioLinkPageModels";
import styles from "./StudioLinkPageSection.module.scss";

interface IStudioLinkPageItemDialogProps {
  visible: boolean;
  onHide: () => void;
  editing?: IStudioLinkPageItem | null;
  isSaving: boolean;
  /** The studio's own domains, comma-separated — links back to them skip the favicon service. */
  ownDomains?: string;
  onSave: (values: IStudioLinkPageItemFormModel) => void;
}

export const StudioLinkPageItemDialog = ({
  visible,
  onHide,
  editing,
  isSaving,
  ownDomains,
  onSave,
}: IStudioLinkPageItemDialogProps) => {
  const form = useForm<IStudioLinkPageItemFormModel>({
    resolver: yupResolver(studioLinkPageItemSchema),
    defaultValues: studioLinkPageItemFormDefaultValues,
  });

  // Remembers the icon this dialog suggested last, so it can tell an untouched field from one
  // the studio filled in itself — the latter is never overwritten.
  const lastSuggestionRef = useRef("");

  // The link's address as it was when the dialog opened. Suggestions only run once the address
  // has moved away from it: reopening a link is not a reason to touch its icon, and an icon the
  // studio deliberately cleared looks exactly like one that was never filled in.
  const urlAtOpenRef = useRef("");

  const url = form.watch("url");

  // Re-seed the form each time the dialog opens (create => blank, edit => existing values).
  useEffect(() => {
    if (!visible) {
      return;
    }

    lastSuggestionRef.current = "";
    urlAtOpenRef.current = editing?.url ?? "";

    if (editing) {
      form.reset({
        id: editing.id,
        name: editing.name,
        url: editing.url,
        iconUrl: editing.iconUrl ?? "",
      });
    } else {
      form.reset(studioLinkPageItemFormDefaultValues);
    }
  }, [visible, editing, form]);

  useEffect(() => {
    if (!visible) {
      return;
    }

    // Read live rather than from the watched `url`: on the render right after the reset above
    // the watched value can still be the previous link's, and a suggestion for that would be
    // written onto this one.
    const currentUrl = form.getValues("url") ?? "";

    if (currentUrl === urlAtOpenRef.current) {
      return;
    }

    const suggestion = resolveSuggestedIcon({
      newUrl: currentUrl,
      currentIcon: form.getValues("iconUrl") ?? "",
      lastSuggestion: lastSuggestionRef.current,
      ownDomains,
    });

    if (suggestion === form.getValues("iconUrl")) {
      return;
    }

    lastSuggestionRef.current = suggestion;
    form.setValue("iconUrl", suggestion);
  }, [visible, url, ownDomains, form]);

  const isEdit = !!editing;

  // This dialog is rendered inside the Studio wizard's <form>, so a nested <form> would be
  // invalid HTML and a submit button would end up submitting the wizard form instead. Submit
  // via an explicit handler and guard Enter from bubbling up, same as StudioQrCodeDialog.
  const submit = form.handleSubmit(onSave);

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === "Enter") {
      event.preventDefault();
      event.stopPropagation();
      submit();
    }
  };

  const iconUrl = form.watch("iconUrl");

  return (
    <AppDialog
      visible={visible}
      onHide={onHide}
      header={isEdit ? "Редакция на линк" : "Нов линк"}
    >
      <FormProvider {...form}>
        <div className={styles.dialog_form} onKeyDown={handleKeyDown}>
          <DefaultInputField
            name={nameof<IStudioLinkPageItemFormModel>((x) => x.name)}
            label="Име на линка"
            placeholder="напр. Instagram, Резервирай час"
            required
            width="100%"
          />

          <DefaultInputField
            name={nameof<IStudioLinkPageItemFormModel>((x) => x.url)}
            label="Линк"
            placeholder="напр. https://instagram.com/mystudio"
            required
            width="100%"
            tooltip="Адресът, към който води бутонът. Ако пропуснете http(s), ще се добави автоматично https://."
          />

          {/* The preview sits before the input, so only the icon field gives up width for it.
              Its slot is always there, empty or not, so the input never shifts sideways when an
              icon appears, disappears or fails to load. */}
          <div className={styles.icon_field}>
            <div
              className={styles.icon_field_preview}
              data-testid="link-icon-preview-slot"
            >
              {!!iconUrl && (
                <img
                  // Remounting on every new address resets the hidden state below, so a broken
                  // icon does not keep the next (working) one invisible.
                  key={iconUrl}
                  className={styles.icon_field_preview_image}
                  src={iconUrl}
                  alt=""
                  data-testid="link-icon-preview"
                  onError={(event) => {
                    event.currentTarget.style.visibility = "hidden";
                  }}
                />
              )}
            </div>

            <DefaultInputField
              className={styles.icon_field_input}
              name={nameof<IStudioLinkPageItemFormModel>((x) => x.iconUrl)}
              label="Икона"
              placeholder="напр. https://cdn.example.com/icon.png"
              width="100%"
              tooltip="Попълва се автоматично от адреса на линка. Можете да я смените или да я изтриете - тогава се показва емоджито или първата буква от името."
            />
          </div>

          <div className={styles.dialog_actions}>
            <DefaultButton
              label="Отказ"
              design="link"
              type="button"
              width="auto"
              onClick={onHide}
            />
            <DefaultButton
              label={isEdit ? "Запази" : "Добави"}
              type="button"
              width="auto"
              loading={isSaving}
              onClick={() => submit()}
            />
          </div>
        </div>
      </FormProvider>
    </AppDialog>
  );
};
